import { useEffect, useState, useCallback } from 'react'
import { apiGet, apiPost } from '../../api'

interface FamilyEvent {
  id: string
  start_at: number
  end_at: number
  title: string
  location: string | null
  is_focus_block: number
  account: string
  ai_urgency: number | null
  ai_reason: string | null
}
interface FamilyResponse {
  events: FamilyEvent[]
  family_calendar_ids: string[]
}
interface CalListEntry {
  id: string
  summary: string
  primary?: boolean
  backgroundColor?: string
  provider?: string
  enabled: boolean
}

const time = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Europe/London' })
const dayLabel = new Intl.DateTimeFormat('en-GB', { weekday: 'long', day: 'numeric', month: 'short', timeZone: 'Europe/London' })

function dayKey(ms: number): string {
  return new Date(ms).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

export default function FamilyCalendar() {
  const [data, setData] = useState<FamilyResponse | null>(null)
  const [pickerOpen, setPickerOpen] = useState(false)
  const [cals, setCals] = useState<CalListEntry[] | null>(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      const res = await apiGet<FamilyResponse>('/calendar/family-events?days=14')
      setData(res)
    } catch (e) { setErr(String(e)) }
  }, [])

  useEffect(() => {
    load()
    const t = setInterval(load, 5 * 60_000)
    return () => clearInterval(t)
  }, [load])

  useEffect(() => {
    if (!pickerOpen || cals !== null) return
    apiGet<{ calendars: CalListEntry[] }>('/calendar/family-list')
      .then(r => setCals(r.calendars))
      .catch(e => setErr(String(e)))
  }, [pickerOpen, cals])

  async function toggle(id: string) {
    if (!cals || busy) return
    const next = cals.map(c => c.id === id ? { ...c, enabled: !c.enabled } : c)
    setCals(next)
    const ids = next.filter(c => c.enabled).map(c => c.primary ? 'primary' : c.id)
    setBusy(true)
    try {
      await apiPost('/calendar/family-select', { ids })
      await load()
    } catch (e) { setErr(String(e)) }
    finally { setBusy(false) }
  }

  // Group events by day for nicer rendering
  const byDay = (data?.events ?? []).reduce<Record<string, FamilyEvent[]>>((acc, ev) => {
    const k = dayKey(ev.start_at)
    if (!acc[k]) acc[k] = []
    acc[k].push(ev)
    return acc
  }, {})

  return (
    <>
      <div className="panel-header" style={{ marginTop: '1rem', position: 'relative' }}>
        <span>Family calendar</span>
        <button type="button" onClick={() => setPickerOpen(v => !v)}
          style={{ minHeight: 26, padding: '2px 10px', fontSize: '0.68rem' }}>
          {pickerOpen ? '×' : '+ calendars'}
        </button>
        {pickerOpen && (
          <div style={{
            position: 'absolute', right: 0, top: '100%', marginTop: 6, zIndex: 20,
            background: 'var(--bg-elevated)', border: '1px solid var(--border-strong)',
            borderRadius: 'var(--radius-md)', padding: '0.6rem 0.7rem',
            boxShadow: '0 8px 28px rgba(0,0,0,0.5)', minWidth: 280, maxHeight: 320, overflowY: 'auto',
            backdropFilter: 'blur(14px)',
          }}>
            <div className="mono" style={{ fontSize: '0.62rem', letterSpacing: '0.14em',
                                            textTransform: 'uppercase', color: 'var(--text-secondary)',
                                            marginBottom: 6 }}>
              Tag as family
            </div>
            {!cals && <div className="muted" style={{ fontSize: '0.75rem' }}>Loading…</div>}
            {cals && cals.length === 0 && <div className="muted" style={{ fontSize: '0.75rem' }}>No Google calendars connected.</div>}
            {cals?.map(c => {
              const tag = c.provider === 'google' ? 'G' : c.provider?.startsWith('ms:') ? '⊞' : '?'
              return (
                <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 8,
                                            padding: '4px 0', fontSize: '0.78rem', cursor: 'pointer' }}>
                  <input type="checkbox" checked={c.enabled} onChange={() => toggle(c.id)} />
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    width: 16, height: 16, borderRadius: 3,
                    fontSize: '0.6rem', fontFamily: 'var(--font-mono)',
                    color: c.provider === 'google' ? '#3ddc84' : 'var(--accent)',
                    background: c.provider === 'google' ? 'rgba(61,220,132,0.12)' : 'rgba(90,242,255,0.12)',
                    border: c.provider === 'google' ? '1px solid rgba(61,220,132,0.3)' : '1px solid rgba(90,242,255,0.3)',
                  }}>{tag}</span>
                  <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {c.summary}{c.primary ? ' (primary)' : ''}
                  </span>
                </label>
              )
            })}
          </div>
        )}
      </div>

      {err && <div className="empty-state">Couldn't load: {err}</div>}
      {!data && !err && <div className="empty-state">Loading…</div>}
      {data && data.family_calendar_ids.length === 0 && (
        <div className="empty-state" style={{ fontSize: '0.78rem' }}>
          No family calendars selected — click <em>+ calendars</em> and tick the family one.
        </div>
      )}
      {data && data.family_calendar_ids.length > 0 && data.events.length === 0 && (
        <div className="empty-state" style={{ fontSize: '0.78rem' }}>Nothing on the family calendar in the next 14 days.</div>
      )}
      {data && data.events.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {Object.entries(byDay).map(([day, events]) => (
            <div key={day}>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.62rem', letterSpacing: '0.16em',
                            textTransform: 'uppercase', color: 'var(--accent-claire)', marginBottom: 4 }}>
                {dayLabel.format(events[0]!.start_at)}
              </div>
              <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
                {events.map(ev => (
                  <li key={ev.id} style={{
                    display: 'flex', alignItems: 'baseline', gap: 8,
                    padding: '5px 8px', borderRadius: 6,
                    background: 'rgba(197,179,255,0.04)',
                    border: '1px solid rgba(197,179,255,0.10)',
                    fontSize: '0.82rem',
                  }} title={ev.ai_reason ?? ''}>
                    <span className="mono" style={{ color: 'var(--text-secondary)', fontSize: '0.7rem',
                                                    flex: '0 0 70px' }}>
                      {time.format(ev.start_at)}–{time.format(ev.end_at)}
                    </span>
                    <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {ev.title}
                      {ev.location && <span className="muted"> · {ev.location}</span>}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </>
  )
}
